/**
 * OpenNebula load balancers + SSL certificates. Endpoints (v2 s10003):
 *   /lbs, /lbs/:id, /lbs/:id/stats, /lbs/:id/perform
 *   /certificates, /certificates/:id
 */
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap, getPollingInterval, type WaitingState } from './_shared';
import type { MonitoringPoint, MonitoringQuery } from './instances/instance-types';

const arr = <T,>(body: unknown, key: string): T[] =>
  Array.isArray(body) ? (body as T[]) : ((body as Record<string, unknown>)?.[key] as T[]) ?? [];

export interface LbServer {
  name?: string;
  host?: string;
  port?: string | number;
  /** Per-server protocol override (defaults to the listener's). */
  protocol?: string;
  backup?: boolean;
  weight?: number | null;
  sni?: string | null;
  proxy?: boolean | string | null;
  proxyversion?: string | null;
  tls_encrypt?: boolean;
}

/** Backend server for an ACL routing rule (L7). */
export interface LbRuleServer {
  host: string;
  port: string;
  mode?: string;
  sni?: string;
}

/** L7 ACL routing rule. `conditions` are `[!]field:operator:value` strings. */
export interface LbAclRule {
  conditions: string[];
  action: string;
  combinator?: 'and' | 'or';
  servers?: LbRuleServer[];
}

/** Per-listener active health-check probe. */
export interface LbHealthCheck {
  enabled: boolean;
  type: 'tcp' | 'http' | 'https' | string;
  interval?: number;
  timeout?: number;
  retries?: number;
  http_path?: string;
  http_expected_status?: number;
  http_host?: string;
  http_version?: '1.0' | '1.1' | string;
}

export interface LbListener {
  name?: string;
  port?: string | number;
  protocol?: 'TCP' | 'UDP' | 'HTTP' | 'HTTPS' | string;
  certificate?: string;
  servers?: LbServer[];
  rules?: LbAclRule[];
  // ---- Advanced (HAProxy) ----
  connect_timeout?: number;
  client_timeout?: number;
  server_timeout?: number;
  custom_headers?: string;
  cors_enabled?: boolean;
  cors_origins?: string;
  alpn_protocols?: string;
  https_ssl_policy?: 'compatible' | 'intermediate' | 'modern' | string;
  https_min_tls?: '1.0' | '1.1' | '1.2' | '1.3' | string;
  health_check?: LbHealthCheck;
  /** X-Forwarded-* header insertion (HTTP/HTTPS). */
  xff_enabled?: boolean;
  xfp_enabled?: boolean;
  xfport_enabled?: boolean;
  /** Sticky session via HAProxy cookie insert (HTTP/HTTPS). */
  sticky_enabled?: boolean;
  sticky_cookie?: string;
}

/** An L4 port forward served by the appliance's LVS service (keepalived + IPVS),
 *  managed independently of HAProxy listeners via `/lbs/:id/forwards`. */
export interface LbForward {
  name?: string;
  port?: string | number;
  protocol?: 'TCP' | 'UDP' | string;
  servers?: LbServer[];
}

export interface LbResources {
  cores?: number;
  memory?: number;
  volumes?: number;
  networks?: number;
}

export interface LbNic {
  nic_id?: string;
  name?: string;
  ip?: string;
  mac?: string;
  network?: string;
  security_groups?: string[];
}

export interface LbMetadata {
  name?: string;
  type?: string;
  /** Flavor code (set on resize; older LBs may only carry `resources`). */
  flavor?: string;
  image?: { name?: string; distro?: string };
  router?: { id?: number; name?: string };
  resources?: LbResources;
  instance_id?: number;
  nics?: LbNic[];
  listeners?: LbListener[];
  /** Present when the LB is provisioned + managed by a Kubernetes cluster.
   *  `service_uuid` is the K8s service that owns the cluster — used to deep-link
   *  the compute LB console back to the cluster's Ingress tab. */
  cluster?: { id?: string; name?: string; version?: string; service_uuid?: string };
}

export interface LoadBalancer {
  id: string;
  name?: string;
  uid?: string;
  type?: string;
  status?: string;
  state?: string;
  ip?: string;
  zone?: string;
  metadata?: LbMetadata;
  created_at?: string;
  updated_at?: string;
}

// ---- Stats ---------------------------------------------------------------

export interface LbStatValue {
  title?: 'Frontend' | 'Server' | 'Backend' | string;
  name?: string;
  proxy_name?: string;
  server_name?: string;
  session_limit?: number | null;
  session_total?: number;
  session_current?: number;
  session_max?: number;
  status?: string;
  byte_in?: number;
  byte_out?: number;
  http_success?: number;
  http_redirect?: number;
  http_client?: number;
  http_error?: number;
  http_other?: number;
}

export interface LbStatGroup {
  id: string;
  proxy_name: string;
  values: LbStatValue[];
}

export function useLoadBalancers(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.lbs(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => arr<LoadBalancer>(unwrap(await serviceApi.get(onPath(serviceId!, 'lbs'))), 'lbs'),
  });
}

export function useLoadBalancer(serviceId: string | undefined, id: string | undefined, waiting: WaitingState = false) {
  return useQuery({
    queryKey: onKeys.lb(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: getPollingInterval(waiting),
    queryFn: async () => {
      // Detail wraps the LB under `instance` (not `lb`).
      const body = unwrap<{ instance?: LoadBalancer } | LoadBalancer>(await serviceApi.get(onPath(serviceId!, `lbs/${id}`)));
      return (body && typeof body === 'object' && 'instance' in body ? body.instance : body) as LoadBalancer;
    },
  });
}

export function useLoadBalancerStats(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.lbStats(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async () => {
      const body = unwrap<{ stats?: LbStatGroup[] } | LbStatGroup[]>(await serviceApi.get(onPath(serviceId!, `lbs/${id}/stats`)));
      return Array.isArray(body) ? body : body.stats ?? [];
    },
  });
}

// ---- Monitoring (VRouter VM perf metrics: CPU / memory / disk / network) --
// Same shape + signature as useInstanceMonitoring — the LB is an OpenNebula VM,
// so the MonitoringTab can be reused as-is by passing this hook.
export function useLoadBalancerMonitoring(
  serviceId: string | undefined,
  id: string | undefined,
  query?: MonitoringQuery,
) {
  return useQuery({
    queryKey: onKeys.lbMonitoring(serviceId ?? '', id ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async () => {
      const body = unwrap<{ monitoring?: MonitoringPoint[] } | MonitoringPoint[]>(
        await serviceApi.get(onPath(serviceId!, `lbs/${id}/monitoring`), { params: query }),
      );
      return Array.isArray(body) ? body : body.monitoring ?? [];
    },
  });
}

export function useCreateLoadBalancer(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, 'lbs'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.lbs(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useUpdateLoadBalancer(serviceId: string, id: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.patch(onPath(serviceId, `lbs/${id}`), p)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.lb(serviceId, id) });
      qc.invalidateQueries({ queryKey: onKeys.lbs(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- L4 port forwards (LVS: keepalived + IPVS) --------------------------------
// Decoupled from HAProxy listeners — these hit /lbs/:id/forwards, which only touches
// the ONEAPP_VNF_LB* context and never the HAProxy config, so a forward change cannot
// affect the HAProxy-served listeners (ingress 80/443/…).

export function useLoadBalancerForwards(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.lbForwards(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => arr<LbForward>(unwrap(await serviceApi.get(onPath(serviceId!, `lbs/${id}/forwards`))), 'forwards'),
  });
}

/** Full-replace the forward set (create = append then PUT, delete = filter then PUT). */
export function useUpdateLoadBalancerForwards(serviceId: string, id: string, options?: UseMutationOptions<unknown, unknown, LbForward[]>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (forwards: LbForward[]) => (await serviceApi.put(onPath(serviceId, `lbs/${id}/forwards`), { forwards })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.lbForwards(serviceId, id) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteLoadBalancer(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.post(onPath(serviceId, `lbs/${id}/perform`), { perform: 'terminate' })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.lbs(serviceId) }); options?.onSuccess?.(...a); },
  });
}

// ---- SSL certificates ----------------------------------------------------

export interface Certificate {
  id: string;
  name?: string;
  /** Single / Wildcard / SAN. */
  type?: string;
  fingerprint?: string;
  created_at?: string;
  updated_at?: string;
}

export function useCertificates(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.certificates(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => arr<Certificate>(unwrap(await serviceApi.get(onPath(serviceId!, 'certificates'))), 'certificates'),
  });
}

/** Upload payload — matches @console `POST /certificates`: PEM `certificate` +
 *  `privkey`, with an optional `chain`. (NOT `private_key`.) */
export interface CertificateUploadPayload {
  name: string;
  certificate: string;
  privkey: string;
  /** Optional intermediate CA chain (PEM). Omit when empty. */
  chain?: string;
}

export function useCreateCertificate(serviceId: string, options?: UseMutationOptions<unknown, unknown, CertificateUploadPayload>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: CertificateUploadPayload) => (await serviceApi.post(onPath(serviceId, 'certificates'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.certificates(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useUpdateCertificate(serviceId: string, id: string, options?: UseMutationOptions<unknown, unknown, { name: string }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { name: string }) => (await serviceApi.put(onPath(serviceId, `certificates/${id}`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.certificates(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteCertificate(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `certificates/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.certificates(serviceId) }); options?.onSuccess?.(...a); },
  });
}
